import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Lock, LogOut, RefreshCw, ShieldCheck, TrendingUp, Users, DollarSign, ShoppingBag, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "qgl_admin_token";

const fmtMoney = (n) =>
    `$${Number(n || 0).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleString("en-CA", { year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
    } catch (e) { return iso; }
};

// ─── Login screen ──────────────────────────────────────────────────
const LoginView = ({ onLogin }) => {
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    const submit = async (e) => {
        e.preventDefault();
        setError(null);
        setBusy(true);
        try {
            const { data } = await axios.post(`${API}/admin/login`, { password });
            sessionStorage.setItem(TOKEN_KEY, data.token);
            onLogin(data.token);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            setError(typeof detail === "string" ? detail : "Login failed. Please try again.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#05050A] text-slate-100 flex items-center justify-center px-5 py-10" data-testid="admin-login-view">
            <div className="w-full max-w-sm">
                <div className="flex items-center gap-2.5 mb-8">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-[0_0_22px_rgba(99,102,241,0.4)]">
                        <ShieldCheck className="w-4 h-4 text-white" />
                    </div>
                    <div className="leading-tight">
                        <p className="text-[10px] tracking-[0.28em] uppercase text-indigo-400">Quantum Genesis Labs</p>
                        <p className="text-sm text-slate-300 font-medium">Referral Stats</p>
                    </div>
                </div>

                <div className="rounded-2xl border border-white/10 bg-[#0A0A12]/70 backdrop-blur-xl p-7">
                    <h1 className="text-2xl font-light tracking-[-0.02em] mb-1">Sign in</h1>
                    <p className="text-sm text-slate-400 mb-6 leading-relaxed">
                        Enter your admin password to view referral statistics.
                    </p>

                    <form onSubmit={submit} className="space-y-4">
                        <div>
                            <label className="text-[10px] tracking-[0.22em] uppercase text-slate-500 mb-2 block">
                                Password
                            </label>
                            <div className="relative">
                                <Lock className="w-4 h-4 text-slate-500 absolute left-3.5 top-1/2 -translate-y-1/2" />
                                <input
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    autoFocus
                                    autoComplete="current-password"
                                    placeholder="••••••••"
                                    data-testid="admin-password-input"
                                    className="w-full h-11 pl-10 pr-3.5 rounded-xl bg-white/[0.03] border border-white/10 focus:border-indigo-400/60 focus:ring-1 focus:ring-indigo-400/40 text-white text-sm outline-none transition-colors"
                                />
                            </div>
                        </div>

                        {error && (
                            <div
                                data-testid="admin-login-error"
                                className="text-[13px] rounded-lg border border-rose-500/30 bg-rose-500/10 text-rose-200 px-3.5 py-2.5"
                            >
                                {error}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={busy || !password}
                            data-testid="admin-login-submit"
                            className="w-full h-11 rounded-full bg-indigo-500 hover:bg-indigo-400 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium tracking-tight transition-colors flex items-center justify-center gap-2"
                        >
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                            {busy ? "Signing in…" : "Sign in"}
                        </button>
                    </form>
                </div>

                <p className="text-[11px] text-slate-600 text-center mt-6 tracking-wide">
                    Session ends when you close this browser tab.
                </p>
            </div>
        </div>
    );
};

// ─── Stat card ─────────────────────────────────────────────────────
const StatCard = ({ icon: Icon, label, value, testid }) => (
    <div className="rounded-xl border border-white/10 bg-[#0A0A12]/70 px-4 py-4 sm:px-5 sm:py-5" data-testid={testid}>
        <div className="flex items-center gap-2 text-slate-400">
            <Icon className="w-3.5 h-3.5" />
            <span className="text-[10px] tracking-[0.2em] uppercase">{label}</span>
        </div>
        <p className="mt-2 text-2xl sm:text-[26px] font-light text-white tracking-tight">
            {value}
        </p>
    </div>
);

// ─── Referral block ────────────────────────────────────────────────
const ReferralBlock = ({ data }) => (
    <section
        className="rounded-2xl border border-white/10 bg-[#0A0A12]/70 overflow-hidden"
        data-testid={`referral-block-${data.code}`}
    >
        <header className="flex items-center justify-between gap-3 px-5 sm:px-6 py-4 border-b border-white/10 bg-gradient-to-r from-indigo-500/[0.08] to-violet-500/[0.06]">
            <div className="min-w-0">
                <p className="text-[10px] tracking-[0.22em] uppercase text-indigo-300">Referral</p>
                <h2 className="text-lg sm:text-xl font-medium text-white truncate">{data.label}</h2>
            </div>
            <code
                className="text-[11px] tracking-tight text-slate-400 bg-white/[0.04] border border-white/10 rounded-md px-2 py-1 shrink-0"
                data-testid={`referral-code-${data.code}`}
            >
                ?ref={data.code}
            </code>
        </header>

        <div className="p-5 sm:p-6 space-y-6">
            {/* Stats grid — 2 cols on mobile, 4 on desktop */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <StatCard
                    icon={Users}
                    label="QR / Link visits"
                    value={data.visits.toLocaleString()}
                    testid={`stat-visits-${data.code}`}
                />
                <StatCard
                    icon={ShoppingBag}
                    label="Order requests"
                    value={data.order_requests.toLocaleString()}
                    testid={`stat-orders-${data.code}`}
                />
                <StatCard
                    icon={DollarSign}
                    label="Total value"
                    value={fmtMoney(data.total_value)}
                    testid={`stat-total-${data.code}`}
                />
                <StatCard
                    icon={TrendingUp}
                    label="Avg order value"
                    value={fmtMoney(data.avg_order_value)}
                    testid={`stat-avg-${data.code}`}
                />
            </div>

            {/* Recent orders */}
            <div>
                <p className="text-[10px] tracking-[0.22em] uppercase text-slate-500 mb-3">
                    Recent order requests
                </p>
                {data.recent_orders.length === 0 ? (
                    <div
                        className="rounded-xl border border-dashed border-white/10 bg-white/[0.02] px-4 py-6 text-center text-sm text-slate-500"
                        data-testid={`recent-empty-${data.code}`}
                    >
                        No orders yet — share your QR code to start tracking.
                    </div>
                ) : (
                    <ul className="divide-y divide-white/5 rounded-xl border border-white/10 overflow-hidden" data-testid={`recent-list-${data.code}`}>
                        {data.recent_orders.map((o) => (
                            <li key={o.id} className="flex items-center gap-3 px-4 py-3 hover:bg-white/[0.02] transition-colors">
                                <div className="min-w-0 flex-1">
                                    <p className="text-sm text-white truncate">{o.name}</p>
                                    <p className="text-[11px] text-slate-500 truncate">
                                        {o.email || "—"} · {o.item_count} {o.item_count === 1 ? "item" : "items"} · {fmtDate(o.created_at)}
                                    </p>
                                </div>
                                <p className="text-sm text-white font-medium shrink-0 tabular-nums">
                                    {fmtMoney(o.total)}
                                </p>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    </section>
);

// ─── Stats view ────────────────────────────────────────────────────
const StatsView = ({ token, onLogout }) => {
    const [state, setState] = useState({ loading: true, data: null, error: null });

    const load = useCallback(async () => {
        setState((s) => ({ ...s, loading: true, error: null }));
        try {
            const { data } = await axios.get(`${API}/admin/referrals/stats`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setState({ loading: false, data, error: null });
        } catch (err) {
            if (err?.response?.status === 401) {
                sessionStorage.removeItem(TOKEN_KEY);
                onLogout();
                return;
            }
            setState({ loading: false, data: null, error: "Could not load stats. Try refreshing." });
        }
    }, [token, onLogout]);

    useEffect(() => { load(); }, [load]);

    const handleLogout = () => {
        sessionStorage.removeItem(TOKEN_KEY);
        onLogout();
    };

    return (
        <div className="min-h-screen bg-[#05050A] text-slate-100" data-testid="admin-stats-view">
            {/* Header */}
            <header className="sticky top-0 z-30 backdrop-blur-xl bg-[#05050A]/85 border-b border-white/5">
                <div className="max-w-4xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2.5 min-w-0">
                        <div className="w-7 h-7 rounded-md bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shrink-0">
                            <ShieldCheck className="w-3.5 h-3.5 text-white" />
                        </div>
                        <div className="min-w-0 leading-tight">
                            <p className="text-[9px] tracking-[0.28em] uppercase text-indigo-400">Quantum Genesis Labs</p>
                            <p className="text-sm text-white font-medium truncate">Referral Stats</p>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        <button
                            onClick={load}
                            disabled={state.loading}
                            data-testid="admin-refresh-btn"
                            className="p-2 rounded-full text-slate-400 hover:text-white hover:bg-white/[0.06] transition-colors disabled:opacity-40"
                            aria-label="Refresh"
                        >
                            {state.loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                        </button>
                        <button
                            onClick={handleLogout}
                            data-testid="admin-logout-btn"
                            className="flex items-center gap-1.5 h-9 px-3 rounded-full text-sm text-slate-300 hover:text-white bg-white/[0.03] hover:bg-white/[0.07] border border-white/10 transition-colors"
                        >
                            <LogOut className="w-3.5 h-3.5" /> Sign out
                        </button>
                    </div>
                </div>
            </header>

            {/* Body */}
            <main className="max-w-4xl mx-auto px-5 sm:px-8 py-6 sm:py-10 space-y-6">
                {state.loading && !state.data && (
                    <div className="rounded-2xl border border-white/10 bg-[#0A0A12]/70 p-10 flex items-center justify-center gap-3 text-slate-400">
                        <Loader2 className="w-4 h-4 animate-spin" /> Loading stats…
                    </div>
                )}

                {state.error && (
                    <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 text-rose-200 px-4 py-3 text-sm">
                        {state.error}
                    </div>
                )}

                {state.data && state.data.referrals && state.data.referrals.map((r) => (
                    <ReferralBlock key={r.code} data={r} />
                ))}

                {state.data && (
                    <p className="text-[11px] text-slate-600 text-center pt-2">
                        Updated {fmtDate(state.data.generated_at)}
                    </p>
                )}
            </main>
        </div>
    );
};

// ─── Root ──────────────────────────────────────────────────────────
const AdminReferrals = () => {
    const [token, setToken] = useState(() => {
        try { return sessionStorage.getItem(TOKEN_KEY); } catch (e) { return null; }
    });

    useEffect(() => {
        // Set a distinct page title for GA4 + browser tab
        document.title = "Referral Stats — Admin";
    }, []);

    if (!token) return <LoginView onLogin={setToken} />;
    return <StatsView token={token} onLogout={() => setToken(null)} />;
};

export default AdminReferrals;
